import React, {ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {distinctUntilChanged, interval, skip} from 'rxjs';
import {map} from 'rxjs/operators';
import {Fade} from '@esgi/ui-kit/transition';
import {join, tryCall} from '@esgi/ui-kit/utils';
import {
	ArrowNavigation,
	ClickOutsideListener,
	useKeyboardEffect,
	getKeyboardFocusableElements,
} from '@esgi/ui-kit/core';
import {PopupAppearAnimation} from './popup-appear-animation';
import style from './dropdown.module.less';

export interface PopupProps {
	toggled: boolean;
	targetRef: RefObject<HTMLElement>;
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
	onLooseFocus?: () => void;
	handleAutoWidth?: (elementWidth: number) => void
	autoWidth?: boolean;
	autoHeight?: boolean;
	children?: ReactNode;
}

function calculateMaxHeight(targetRef: RefObject<HTMLElement>, containerRef: RefObject<HTMLElement>, contentRef: RefObject<HTMLElement>): void {
	if (containerRef.current && contentRef.current && targetRef.current) {
		const {bottom} = targetRef.current.getBoundingClientRect();
		const scrollHeight = contentRef.current.scrollHeight;
		const windowHeight = document.body.offsetHeight;
		if (containerRef.current.dataset.dropdown === 'top') {
			containerRef.current.style.maxHeight = scrollHeight + 30 + 'px';
			return;
		}

		let maxHeight = windowHeight - bottom - 30;
		if (maxHeight > scrollHeight) {
			maxHeight = scrollHeight + 30;
		}
		containerRef.current.style.maxHeight = maxHeight + 'px';
	}
}

function calculateAutoHeight(targetRef: RefObject<HTMLElement>, containerRef: RefObject<HTMLElement>, contentRef: RefObject<HTMLElement>): void {
	if (containerRef.current && contentRef.current && targetRef.current) {
		containerRef.current.style.maxHeight = 'auto';
		const {bottom} = targetRef.current.getBoundingClientRect();
		const scrollHeight = contentRef.current.scrollHeight;
		const windowHeight = document.body.offsetHeight;
		if (containerRef.current.dataset.dropdown === 'top') {
			containerRef.current.style.maxHeight = scrollHeight + 30 + 'px';
			return;
		}

		let maxHeight = windowHeight - bottom - 30;
		if (maxHeight < scrollHeight) {
			maxHeight = maxHeight - 30;
		}
		containerRef.current.style.maxHeight = maxHeight + 'px';
	}
}

function recalculateElementPosition(target: RefObject<HTMLElement>, container: RefObject<HTMLElement>, content: RefObject<HTMLElement>): void {
	if (!target.current || !content.current || !container.current) {
		return;
	}

	const domRect = target.current.getBoundingClientRect();

	const containerRect = container.current.getBoundingClientRect();
	const targetBottom = document.body.offsetHeight - domRect.top - domRect.height;
	const contentHeight = content.current.scrollHeight;

	if (targetBottom < contentHeight && contentHeight < domRect.top) {
		container.current.style.top = (domRect.top - content.current.scrollHeight) + 'px';
		container.current.style.left = domRect.left + 'px';
		container.current.dataset.dropdown = 'top';
	} else if (containerRect.top !== domRect.bottom - 5 || containerRect.left !== domRect.left - 5) {
		container.current.style.top = domRect.bottom + 'px';
		container.current.style.left = domRect.left + 'px';
		container.current.dataset.dropdown = 'bottom';
	}
}

export function Popup(props: PopupProps): JSX.Element {
	const open = props.toggled;
	const contentRef = useRef<HTMLDivElement>();
	const containerRef = useRef<HTMLDivElement>();

	const [width, setWidth] = useState('auto');

	const onLooseFocus = useCallback(() => props.toggled && tryCall(props.onLooseFocus), [props.toggled, props.onLooseFocus]);

	const handleKeyboard = useCallback((e: KeyboardEvent) => {
		if (props.toggled) {
			if (e.key === 'Tab') {
				const currentActive = document.activeElement;
				const focusableElements = getKeyboardFocusableElements(containerRef.current);
				const activeIndex = focusableElements.indexOf(currentActive);
				if (activeIndex >= 0 && activeIndex === focusableElements.length - 1) {
					tryCall(props.onLooseFocus);
				}
			}

			if (e.key === 'Escape') {
				tryCall(props.onLooseFocus);
			}
		}
	}, [props.toggled, props.onLooseFocus]);
	useKeyboardEffect('keydown', handleKeyboard);

	const recalculateBounds = useCallback(() => {
		recalculateElementPosition(props.targetRef, containerRef, contentRef);
		if (props.autoHeight) {
			calculateAutoHeight(props.targetRef, containerRef, contentRef);
		} else {
			calculateMaxHeight(props.targetRef, containerRef, contentRef);
		}

		if (props.targetRef.current) {
			const target = props.targetRef.current;
			if (props.autoWidth || !target.clientWidth) {
				if (width !== 'auto') {
					setWidth('auto');
				} else {
					requestAnimationFrame(() => {
						if (containerRef.current) {
							const containerBounds = containerRef.current.getBoundingClientRect();
							tryCall(props.handleAutoWidth, containerBounds.width - 20);
						}
					});
				}
			} else {
				setWidth(target.clientWidth + 'px');
			}
		}
	}, [props.targetRef, props.autoHeight, props.autoWidth, props.handleAutoWidth, width]);

	useEffect(() => {
		window.addEventListener('resize', recalculateBounds);
		return () => window.removeEventListener('resize', recalculateBounds);
	}, [recalculateBounds]);

	useLayoutEffect(recalculateBounds, [open, recalculateBounds]);

	useEffect(() => {
		if (props.children) {
			recalculateBounds();
		}
	}, [recalculateBounds, props.children]);

	useEffect(() => {
		if (open) {
			const sub = interval(100).pipe(
				map(() => {
					const bounds = props.targetRef.current?.getBoundingClientRect();
					return {
						x: bounds?.x,
						y: bounds?.y,
					};
				}),
				distinctUntilChanged((prev, curr) => prev.y === curr.y && prev.x === curr.x),
				skip(1),
			).subscribe(() => {
				tryCall(props.onLooseFocus);
			});
			return () => sub.unsubscribe();
		}
	}, [open]);

	return (
		<Fade in={open} duration='fast'>
			<div ref={containerRef} className={style.expandContainer} aria-expanded={open} aria-hidden={!open}>
				<ClickOutsideListener onClickedOutside={onLooseFocus} active={open}>
					<ArrowNavigation disable={!open}>
						<PopupAppearAnimation in={open} duration='fast'>
							<div className={join(style.expand, props.autoHeight && style.autoHeight, props.className)}
									 style={{width}}>
								<div className={style.content} ref={contentRef} role='menu'>
									{props.children}
								</div>
							</div>
						</PopupAppearAnimation>
					</ArrowNavigation>
				</ClickOutsideListener>
			</div>
		</Fade>
	);
}
